import React, {
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { observer } from 'mobx-react-lite';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  PATHS,
  ENTRY_CASES,
  SEARCH_PARAMS,
} from 'src/constants/navigateRoutes';
import { useAuthStore } from 'src/stores/AuthStore/AuthStore';
import { getSessionDetails } from 'src/services/auth';
import { Box } from '@mui/material';
import CustomLoading from '../custom-loading';
import styleVariables from '../../scss/_variables.scss';
import { useOrganizationStore } from 'src/stores/OrganizationStore/OrganizationStore';
import { useAlertStore } from 'src/stores/AlertStore/AlertStore';
import { UNHANDLED_ERROR } from 'src/constants/errors';
import { getLatestPoliciesNotApprovedByMongoUserId } from 'src/services/policy';
import {
  checkIfAccountDeactivated,
  getAllOrganizationsUserIsMemberOf,
} from 'src/services/organization';
import { ROLES } from 'src/constants/Role';
import { getNavigatePathWithRedirectUrl } from 'src/utils/commonValidation';
import { shouldShowChatWithHanaModule } from 'src/utils/chatWithHana';
import { notifyUnhandledError } from 'src/utils/commonErrorUtils';
import { markOnboardingCelebrationPending } from 'src/utils/onboarding';
import { GTMProvider } from '../gtm-provider/GTMProvider';
import {
  DASHBOARD_UNAUTHORIZED_REASON_CODES,
  DashboardUnauthorizedReasonCode,
} from 'src/constants/dashboardAuth';
import { PolicyApiResponse } from 'src/types/policy';

interface ProtectedRouteProps {
  children: ReactNode;
}

export interface PolicyApprovalNavigationState {
  latestPoliciesNotApprovedByUser?: PolicyApiResponse[];
}

const PATHS_REQUIRING_ROUTE_REEVALUATION = new Set([
  PATHS.noActiveOrganization,
  PATHS.onBoardRegistration,
  PATHS.policyApproval,
  PATHS.reactivateAccount,
]);

const KNOWN_UNAUTHORIZED_REASONS = new Set<DashboardUnauthorizedReasonCode>(
  Object.values(DASHBOARD_UNAUTHORIZED_REASON_CODES),
);

const ProtectedRoute: React.FC<ProtectedRouteProps> = observer(
  ({ children }) => {
    const {
      logout,
      fetchUserDetails,
      getOnBoardingCompleteStatus,
      isRevampExperience,
    } = useAuthStore();
    const navigate = useNavigate();
    const location = useLocation();
    const {
      fetchCurrentOrganization,
      fetchSupportedLanguages,
      resetOrganizationState,
    } = useOrganizationStore();
    const [isPageLoading, setIsPageLoading] = useState(true);
    const { notify } = useAlertStore();

    const languagesAllowedRoles: Array<keyof typeof ROLES> = [
      ROLES.ADMIN,
      ROLES.SUPER_ADMIN,
      ROLES.DOMAIN_OWNER,
    ];

    const fetchUserData = useCallback(async () => {
      let shouldFinishLoading = true;

      try {
        const userAccessedUrl = window.location.href;

        const encodedUserAccessedUrl = encodeURIComponent(userAccessedUrl);
        const currentPath = location.pathname;

        const navigateWithinDashboard = (
          targetPath: string,
          options?: { replace?: boolean; keepLoading?: boolean },
        ) => {
          shouldFinishLoading = false;

          if (!options?.keepLoading) {
            setIsPageLoading(false);
          }

          navigate(targetPath, { replace: options?.replace ?? true });
        };

        const redirectToUnauthorized = async (
          reason?: DashboardUnauthorizedReasonCode | null,
        ) => {
          shouldFinishLoading = false;
          await logout();
          navigate(
            getNavigatePathWithRedirectUrl(
              PATHS.unAuthorized,
              currentPath,
              encodedUserAccessedUrl,
              {
                [SEARCH_PARAMS.reason]: reason,
              },
            ),
          );
        };

        const [sessionDetailsResponse, userInfo, accountDeactivatedResponse] =
          await Promise.all([
            getSessionDetails(),
            fetchUserDetails(),
            checkIfAccountDeactivated(),
          ]);

        const {
          success: getSessionInfoSuccess,
          content: sessionInfo,
          errorCode: sessionErrorCode,
        } = sessionDetailsResponse;
        const sessionUnauthorizedReason =
          typeof sessionErrorCode === 'string' &&
          KNOWN_UNAUTHORIZED_REASONS.has(
            sessionErrorCode as DashboardUnauthorizedReasonCode,
          )
            ? (sessionErrorCode as DashboardUnauthorizedReasonCode)
            : sessionInfo?.requiresReauthorization
              ? DASHBOARD_UNAUTHORIZED_REASON_CODES.SCOPES_MISSING
              : null;

        if (!getSessionInfoSuccess || !sessionInfo?.isSessionUsable) {
          await redirectToUnauthorized(sessionUnauthorizedReason);

          return;
        }

        if (!userInfo) {
          await redirectToUnauthorized();

          return;
        }

        const {
          success: isAccountDeactivatedSuccess,
          content: isAccountDeactivated,
          message: accountDeactivatedMessage,
        } = accountDeactivatedResponse;

        if (
          !isAccountDeactivatedSuccess ||
          isAccountDeactivated === undefined
        ) {
          notify(accountDeactivatedMessage ?? UNHANDLED_ERROR, 'error');

          return;
        }

        if (isAccountDeactivated) {
          resetOrganizationState();
          await redirectToUnauthorized(
            DASHBOARD_UNAUTHORIZED_REASON_CODES.ACCOUNT_DEACTIVATED,
          );

          return;
        }

        if (
          currentPath === PATHS.noActiveOrganization ||
          currentPath === PATHS.policyApproval
        ) {
          return;
        }

        if (currentPath === PATHS.onBoardRegistration) {
          const onboardingEntry = new URLSearchParams(location.search).get(
            SEARCH_PARAMS.entry,
          );
          const isOnboardingComplete = await getOnBoardingCompleteStatus();

          if (onboardingEntry === ENTRY_CASES.newUser && isRevampExperience()) {
            navigateWithinDashboard(PATHS.getStarted);

            return;
          }

          if (
            onboardingEntry === ENTRY_CASES.newUser &&
            isOnboardingComplete === true
          ) {
            markOnboardingCelebrationPending();
            navigateWithinDashboard(PATHS.getStarted);

            return;
          }

          return;
        }

        const [
          activeOrgResponse,
          latestPoliciesResponse,
          isOnboardingComplete,
        ] = await Promise.all([
          fetchCurrentOrganization(),
          getLatestPoliciesNotApprovedByMongoUserId(),
          getOnBoardingCompleteStatus(),
        ]);

        if (!activeOrgResponse.success || !activeOrgResponse.content) {
          const isMissingActiveOrganization =
            activeOrgResponse.errorCode ===
              DASHBOARD_UNAUTHORIZED_REASON_CODES.ORG_NOT_FOUND ||
            activeOrgResponse.errorCode ===
              DASHBOARD_UNAUTHORIZED_REASON_CODES.MEMBERSHIP_LOOKUP_FAILED;

          if (isMissingActiveOrganization) {
            resetOrganizationState();
            navigateWithinDashboard(PATHS.noActiveOrganization, {
              keepLoading: true,
            });

            return;
          }

          const { success, content } = await getAllOrganizationsUserIsMemberOf(
            1,
            1,
          );

          if (!success || !content || content.data.length === 0) {
            navigateWithinDashboard(
              `${PATHS.onBoardRegistration}?entry=${ENTRY_CASES.newOrg}`,
              {
                keepLoading: true,
              },
            );

            return;
          }

          navigateWithinDashboard(PATHS.noActiveOrganization, {
            keepLoading: true,
          });

          return;
        }

        if (languagesAllowedRoles.includes(userInfo.role)) {
          void fetchSupportedLanguages();
        }

        const {
          success: getLatestPoliciesSuccess,
          content: latestPoliciesNotApprovedByUser,
          message: latestPoliciesMessage,
        } = latestPoliciesResponse;

        if (!getLatestPoliciesSuccess || !latestPoliciesNotApprovedByUser) {
          notify(latestPoliciesMessage ?? UNHANDLED_ERROR, 'error');

          return;
        }

        if (latestPoliciesNotApprovedByUser.length > 0) {
          if (currentPath === PATHS.policyApproval) {
            return;
          }

          shouldFinishLoading = false;
          navigate(
            getNavigatePathWithRedirectUrl(
              PATHS.policyApproval,
              currentPath,
              encodedUserAccessedUrl,
            ),
            {
              state: {
                latestPoliciesNotApprovedByUser,
              } satisfies PolicyApprovalNavigationState,
            },
          );

          return;
        }

        const canAccessChatWithHana = shouldShowChatWithHanaModule(
          userInfo.email,
        );
        const isOnOnboardingRoute = currentPath === PATHS.onBoardRegistration;

        if (
          isOnboardingComplete === false &&
          !isOnOnboardingRoute &&
          !isRevampExperience()
        ) {
          navigateWithinDashboard(
            `${PATHS.onBoardRegistration}?entry=${ENTRY_CASES.newUser}`,
            {
              keepLoading: true,
            },
          );

          return;
        }

        const isOnChatRoute =
          currentPath === PATHS.chatWithHana ||
          currentPath.startsWith(`${PATHS.chatWithHana}/`);

        if (!canAccessChatWithHana && isOnChatRoute) {
          shouldFinishLoading = false;
          navigateWithinDashboard(PATHS.overview);

          return;
        }
      } catch (error) {
        await notifyUnhandledError(error, notify);
      } finally {
        if (shouldFinishLoading) {
          setIsPageLoading(false);
        }
      }
    }, [
      fetchCurrentOrganization,
      fetchSupportedLanguages,
      fetchUserDetails,
      getOnBoardingCompleteStatus,
      isRevampExperience,
      location.pathname,
      logout,
      navigate,
      notify,
      resetOrganizationState,
    ]);

    const lastFetchedPathRef = useRef<string | null>(null);

    useEffect(() => {
      const lastFetchedPath = lastFetchedPathRef.current;
      const currentPath = location.pathname;
      const shouldReevaluateForRouteChange =
        !lastFetchedPath ||
        PATHS_REQUIRING_ROUTE_REEVALUATION.has(lastFetchedPath) ||
        PATHS_REQUIRING_ROUTE_REEVALUATION.has(currentPath);

      if (lastFetchedPath === currentPath || !shouldReevaluateForRouteChange) {
        return;
      }

      lastFetchedPathRef.current = currentPath;
      setIsPageLoading(true);
      fetchUserData();
    }, [fetchUserData, location.pathname]);

    if (isPageLoading) {
      return (
        <Box
          sx={{
            height: '100vh',
            overflow: 'hidden',
            background: styleVariables.chineseBlack,
          }}
        >
          <CustomLoading />
        </Box>
      );
    }

    return <GTMProvider>{!isPageLoading && children}</GTMProvider>;
  },
);

export default ProtectedRoute;
